export type MarketIntervalT = '1m' | '5m' | '15m' | '1h' | '4h' | '1d';

export type MarketTradeSideT = 'buy' | 'sell';
export type MarketLiquidityHeatmapSideT = 'ask' | 'bid';
export type MarketHeatmapCoverageStatusT = 'empty' | 'full' | 'partial';
export type MarketHeatmapCaptureBasisT = 'bucket_history' | 'event_history';
export type MarketHeatmapValueKindT = 'max_size' | 'notional';

export interface MarketTrade {
  asset: string;
  notional: number;
  price: number;
  side: MarketTradeSideT;
  size: number;
  timestampMs: number;
  tradeId: string;
  txHash: string;
}

export interface MarketChartBar {
  asset: string;
  closePrice: number;
  closeTimeMs: number;
  highPrice: number;
  interval: MarketIntervalT;
  lowPrice: number;
  openPrice: number;
  openTimeMs: number;
  tradeCount: number;
  volume: number;
}

export interface MarketStats {
  asset: string;
  dayNotionalVolume: number;
  fundingRate: number;
  imbalanceRatio: number;
  markPrice: number;
  midPrice: number;
  openInterest: number;
  oraclePrice: number;
  prevDayPrice: number;
  spreadBps: number;
  timestampMs: number;
}

export interface MarketLadderLevel {
  cumulative: number;
  price: number;
  size: number;
}

export interface MarketLadder {
  asset: string;
  asks: MarketLadderLevel[];
  bestAskPrice: number;
  bestBidPrice: number;
  bids: MarketLadderLevel[];
  grouping: number;
  levels: number;
  midPrice: number;
  sequence: number;
  spreadBps: number;
  timestampMs: number;
}

export interface MarketHeatmapScale {
  kind: 'rolling_percentile';
  maxObservedValue: number;
  p50Value: number;
  p90Value: number;
  p95Value: number;
  p99Value: number;
  valueKind: MarketHeatmapValueKindT;
}

export interface MarketHeatmapCoverage {
  bucketDurationMs: number;
  captureBasis: MarketHeatmapCaptureBasisT;
  captureEndTimeMs: null | number;
  captureStartTimeMs: null | number;
  capturedBucketCount: number;
  coverageRatio: number;
  requestedWindowEndMs: number;
  requestedWindowMinutes: number;
  requestedWindowStartMs: number;
  status: MarketHeatmapCoverageStatusT;
}

export type MarketLiquidityHeatmapAnnotationTypeT = 'liquidity_wall';

export interface MarketLiquidityHeatmapAnnotation {
  distanceFromMidBps: number;
  endTimeMs: number;
  persistence: number;
  price: number;
  side: MarketLiquidityHeatmapSideT;
  startTimeMs: number;
  strength: number;
  type: MarketLiquidityHeatmapAnnotationTypeT;
}

export interface MarketLiquidityHeatmapCell {
  avgSize: number;
  intensity: number;
  maxSize: number;
  persistence: number;
  presentSampleCount: number;
  price: number;
  sampleCount: number;
  side: MarketLiquidityHeatmapSideT;
}

export interface MarketLiquidityHeatmapBucket {
  bestAskPrice: number;
  bestBidPrice: number;
  cells: MarketLiquidityHeatmapCell[];
  midPrice: number;
  timeEndMs: number;
  timeStartMs: number;
}

export interface MarketLiquidityHeatmapSnapshot {
  annotations: MarketLiquidityHeatmapAnnotation[];
  asset: string;
  bucketDurationMs: number;
  buckets: MarketLiquidityHeatmapBucket[];
  coverage: MarketHeatmapCoverage;
  priceBucketSize: number;
  scale: MarketHeatmapScale;
  snapshotTimeMs: number;
  version: number;
  windowMinutes: number;
}

export interface MarketErrorMessage {
  error: string;
  type: 'error';
}

export interface MarketHeartbeatMessage {
  timestampMs: number;
  type: 'heartbeat';
}

export interface MarketStreamResources {
  bars?: {
    interval: MarketIntervalT;
  };
  ladder?: {
    grouping: number;
    levels: number;
  };
  liquidity?: {
    windowMinutes: number;
  };
  stats?: true;
  trades?: {
    limit: number;
  };
}

export interface MarketStreamSubscription {
  asset: string;
  resources: MarketStreamResources;
  type: 'subscribe_market';
}

export interface MarketStatsSnapshotMessage {
  asset: string;
  snapshotTimeMs: number;
  stats: MarketStats | null;
  type: 'market.stats_snapshot';
  version: number;
}

export interface MarketStatsMessage {
  asset: string;
  snapshotTimeMs: number;
  stats: MarketStats;
  type: 'market.stats';
  version: number;
}

export interface MarketTradesSnapshotMessage {
  asset: string;
  snapshotTimeMs: number;
  trades: MarketTrade[];
  type: 'market.trades_snapshot';
  version: number;
}

export interface MarketTradesAppendMessage {
  asset: string;
  snapshotTimeMs: number;
  trades: MarketTrade[];
  type: 'market.trades_append';
  version: number;
}

export interface MarketLadderSnapshotMessage {
  asset: string;
  grouping: number;
  ladder: MarketLadder | null;
  levels: number;
  snapshotTimeMs: number;
  type: 'market.ladder_snapshot';
  version: number;
}

export interface MarketLadderMessage {
  asset: string;
  grouping: number;
  ladder: MarketLadder | null;
  levels: number;
  snapshotTimeMs: number;
  type: 'market.ladder';
  version: number;
}

export interface MarketBarsSnapshotMessage {
  asset: string;
  bars: MarketChartBar[];
  interval: MarketIntervalT;
  snapshotTimeMs: number;
  type: 'market.bars_snapshot';
  version: number;
}

export interface MarketBarMessage {
  asset: string;
  bar: MarketChartBar;
  interval: MarketIntervalT;
  snapshotTimeMs: number;
  type: 'market.bar';
  version: number;
}

export interface MarketLiquidityHeatmapSnapshotMessage
  extends MarketLiquidityHeatmapSnapshot {
  type: 'market.liquidity_snapshot';
}

export interface MarketLiquidityHeatmapDeltaMessage {
  annotations: MarketLiquidityHeatmapAnnotation[];
  asset: string;
  coverage: MarketHeatmapCoverage;
  latestBucket: MarketLiquidityHeatmapBucket;
  priceBucketSize: number;
  scale: MarketHeatmapScale;
  snapshotTimeMs: number;
  type: 'market.liquidity_delta';
  version: number;
  windowMinutes: number;
}

export type MarketDomainServerMessage =
  | MarketBarMessage
  | MarketBarsSnapshotMessage
  | MarketErrorMessage
  | MarketHeartbeatMessage
  | MarketLadderMessage
  | MarketLadderSnapshotMessage
  | MarketLiquidityHeatmapDeltaMessage
  | MarketLiquidityHeatmapSnapshotMessage
  | MarketStatsMessage
  | MarketStatsSnapshotMessage
  | MarketTradesAppendMessage
  | MarketTradesSnapshotMessage;

export function isMarketDomainServerMessage(
  value: unknown,
): value is MarketDomainServerMessage {
  if (!isRecord(value)) {
    return false;
  }

  const type = Reflect.get(value, 'type');

  switch (type) {
    case 'market.stats_snapshot':
      return isMarketStatsSnapshotMessage(value);
    case 'market.stats':
      return isMarketStatsMessage(value);
    case 'market.trades_snapshot':
      return isMarketTradesSnapshotMessage(value);
    case 'market.trades_append':
      return isMarketTradesAppendMessage(value);
    case 'market.ladder_snapshot':
      return isMarketLadderSnapshotMessage(value);
    case 'market.ladder':
      return isMarketLadderMessage(value);
    case 'market.bars_snapshot':
      return isMarketBarsSnapshotMessage(value);
    case 'market.bar':
      return isMarketBarMessage(value);
    case 'market.liquidity_snapshot':
      return isMarketLiquidityHeatmapSnapshotMessage(value);
    case 'market.liquidity_delta':
      return isMarketLiquidityHeatmapDeltaMessage(value);
    case 'heartbeat':
      return isMarketHeartbeatMessage(value);
    case 'error':
      return isMarketErrorMessage(value);
    default:
      return false;
  }
}

function isMarketInterval(value: unknown): value is MarketIntervalT {
  switch (value) {
    case '1m':
    case '5m':
    case '15m':
    case '1h':
    case '4h':
    case '1d':
      return true;
    default:
      return false;
  }
}

function isMarketLadderLevel(value: unknown): value is MarketLadderLevel {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isPositiveNumber(Reflect.get(value, 'price')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'size')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'cumulative'))
  );
}

function isMarketLadder(value: unknown): value is MarketLadder {
  if (!isRecord(value)) {
    return false;
  }

  const asks = Reflect.get(value, 'asks');
  const bids = Reflect.get(value, 'bids');

  return (
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isPositiveNumber(Reflect.get(value, 'bestAskPrice')) &&
    isPositiveNumber(Reflect.get(value, 'bestBidPrice')) &&
    isPositiveNumber(Reflect.get(value, 'grouping')) &&
    isPositiveInteger(Reflect.get(value, 'levels')) &&
    isPositiveNumber(Reflect.get(value, 'midPrice')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'sequence')) &&
    isFiniteNumber(Reflect.get(value, 'spreadBps')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'timestampMs')) &&
    Array.isArray(asks) &&
    asks.every((level) => isMarketLadderLevel(level)) &&
    Array.isArray(bids) &&
    bids.every((level) => isMarketLadderLevel(level))
  );
}

function isMarketChartBar(value: unknown): value is MarketChartBar {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isMarketInterval(Reflect.get(value, 'interval')) &&
    isPositiveNumber(Reflect.get(value, 'openPrice')) &&
    isPositiveNumber(Reflect.get(value, 'highPrice')) &&
    isPositiveNumber(Reflect.get(value, 'lowPrice')) &&
    isPositiveNumber(Reflect.get(value, 'closePrice')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'volume')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'tradeCount')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'openTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'closeTimeMs'))
  );
}

function isMarketErrorMessage(value: unknown): value is MarketErrorMessage {
  if (!isRecord(value)) {
    return false;
  }

  return (
    Reflect.get(value, 'type') === 'error' &&
    isNonEmptyString(Reflect.get(value, 'error'))
  );
}

function isMarketHeartbeatMessage(
  value: unknown,
): value is MarketHeartbeatMessage {
  if (!isRecord(value)) {
    return false;
  }

  return (
    Reflect.get(value, 'type') === 'heartbeat' &&
    isPositiveIntegerOrZero(Reflect.get(value, 'timestampMs'))
  );
}

function isMarketStats(value: unknown): value is MarketStats {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isFiniteNumber(Reflect.get(value, 'fundingRate')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'openInterest')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'dayNotionalVolume')) &&
    isPositiveNumber(Reflect.get(value, 'markPrice')) &&
    isPositiveNumber(Reflect.get(value, 'midPrice')) &&
    isPositiveNumber(Reflect.get(value, 'oraclePrice')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'prevDayPrice')) &&
    isFiniteNumber(Reflect.get(value, 'spreadBps')) &&
    isFiniteNumber(Reflect.get(value, 'imbalanceRatio')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'timestampMs'))
  );
}

function isMarketStatsSnapshotMessage(
  value: unknown,
): value is MarketStatsSnapshotMessage {
  if (!isRecord(value)) {
    return false;
  }

  const stats = Reflect.get(value, 'stats');

  return (
    Reflect.get(value, 'type') === 'market.stats_snapshot' &&
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'snapshotTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'version')) &&
    (stats === null || isMarketStats(stats))
  );
}

function isMarketStatsMessage(value: unknown): value is MarketStatsMessage {
  if (!isRecord(value)) {
    return false;
  }

  return (
    Reflect.get(value, 'type') === 'market.stats' &&
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'snapshotTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'version')) &&
    isMarketStats(Reflect.get(value, 'stats'))
  );
}

function isMarketTrade(value: unknown): value is MarketTrade {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isMarketTradeSide(Reflect.get(value, 'side')) &&
    isPositiveNumber(Reflect.get(value, 'price')) &&
    isPositiveNumber(Reflect.get(value, 'size')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'notional')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'timestampMs')) &&
    isNonEmptyString(Reflect.get(value, 'tradeId')) &&
    isNonEmptyString(Reflect.get(value, 'txHash'))
  );
}

function isMarketTradesSnapshotMessage(
  value: unknown,
): value is MarketTradesSnapshotMessage {
  if (!isRecord(value)) {
    return false;
  }

  const trades = Reflect.get(value, 'trades');

  return (
    Reflect.get(value, 'type') === 'market.trades_snapshot' &&
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'snapshotTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'version')) &&
    Array.isArray(trades) &&
    trades.every((trade) => isMarketTrade(trade))
  );
}

function isMarketTradesAppendMessage(
  value: unknown,
): value is MarketTradesAppendMessage {
  if (!isRecord(value)) {
    return false;
  }

  const trades = Reflect.get(value, 'trades');

  return (
    Reflect.get(value, 'type') === 'market.trades_append' &&
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'snapshotTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'version')) &&
    Array.isArray(trades) &&
    trades.every((trade) => isMarketTrade(trade))
  );
}

function isMarketLadderSnapshotMessage(
  value: unknown,
): value is MarketLadderSnapshotMessage {
  if (!isRecord(value)) {
    return false;
  }

  return (
    Reflect.get(value, 'type') === 'market.ladder_snapshot' &&
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isPositiveNumber(Reflect.get(value, 'grouping')) &&
    isPositiveInteger(Reflect.get(value, 'levels')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'snapshotTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'version')) &&
    (Reflect.get(value, 'ladder') === null ||
      isMarketLadder(Reflect.get(value, 'ladder')))
  );
}

function isMarketLadderMessage(value: unknown): value is MarketLadderMessage {
  if (!isRecord(value)) {
    return false;
  }

  return (
    Reflect.get(value, 'type') === 'market.ladder' &&
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isPositiveNumber(Reflect.get(value, 'grouping')) &&
    isPositiveInteger(Reflect.get(value, 'levels')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'snapshotTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'version')) &&
    (Reflect.get(value, 'ladder') === null ||
      isMarketLadder(Reflect.get(value, 'ladder')))
  );
}

function isMarketBarsSnapshotMessage(
  value: unknown,
): value is MarketBarsSnapshotMessage {
  if (!isRecord(value)) {
    return false;
  }

  const bars = Reflect.get(value, 'bars');
  const interval = Reflect.get(value, 'interval');

  if (!isMarketInterval(interval)) {
    return false;
  }

  return (
    Reflect.get(value, 'type') === 'market.bars_snapshot' &&
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'snapshotTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'version')) &&
    Array.isArray(bars) &&
    bars.every((bar) => isMarketChartBar(bar) && bar.interval === interval)
  );
}

function isMarketBarMessage(value: unknown): value is MarketBarMessage {
  if (!isRecord(value)) {
    return false;
  }

  const interval = Reflect.get(value, 'interval');
  const bar = Reflect.get(value, 'bar');

  return (
    Reflect.get(value, 'type') === 'market.bar' &&
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isMarketInterval(interval) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'snapshotTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'version')) &&
    isMarketChartBar(bar) &&
    bar.interval === interval
  );
}

function isMarketHeatmapScale(value: unknown): value is MarketHeatmapScale {
  if (!isRecord(value)) {
    return false;
  }

  return (
    Reflect.get(value, 'kind') === 'rolling_percentile' &&
    isMarketHeatmapValueKind(Reflect.get(value, 'valueKind')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'maxObservedValue')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'p50Value')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'p90Value')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'p95Value')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'p99Value'))
  );
}

function isMarketHeatmapCoverage(
  value: unknown,
): value is MarketHeatmapCoverage {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isPositiveIntegerOrZero(Reflect.get(value, 'bucketDurationMs')) &&
    isMarketHeatmapCaptureBasis(Reflect.get(value, 'captureBasis')) &&
    isNullablePositiveIntegerOrZero(Reflect.get(value, 'captureEndTimeMs')) &&
    isNullablePositiveIntegerOrZero(Reflect.get(value, 'captureStartTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'capturedBucketCount')) &&
    isNormalizedNumber(Reflect.get(value, 'coverageRatio')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'requestedWindowEndMs')) &&
    isPositiveInteger(Reflect.get(value, 'requestedWindowMinutes')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'requestedWindowStartMs')) &&
    isMarketHeatmapCoverageStatus(Reflect.get(value, 'status'))
  );
}

function isMarketLiquidityHeatmapAnnotation(
  value: unknown,
): value is MarketLiquidityHeatmapAnnotation {
  if (!isRecord(value)) {
    return false;
  }

  return (
    Reflect.get(value, 'type') === 'liquidity_wall' &&
    isMarketLiquidityHeatmapSide(Reflect.get(value, 'side')) &&
    isPositiveNumber(Reflect.get(value, 'price')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'startTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'endTimeMs')) &&
    isNormalizedNumber(Reflect.get(value, 'strength')) &&
    isNormalizedNumber(Reflect.get(value, 'persistence')) &&
    isFiniteNumber(Reflect.get(value, 'distanceFromMidBps'))
  );
}

function isMarketLiquidityHeatmapCell(
  value: unknown,
): value is MarketLiquidityHeatmapCell {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isPositiveNumberOrZero(Reflect.get(value, 'avgSize')) &&
    isNormalizedNumber(Reflect.get(value, 'intensity')) &&
    isPositiveNumberOrZero(Reflect.get(value, 'maxSize')) &&
    isNormalizedNumber(Reflect.get(value, 'persistence')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'presentSampleCount')) &&
    isPositiveNumber(Reflect.get(value, 'price')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'sampleCount')) &&
    isMarketLiquidityHeatmapSide(Reflect.get(value, 'side'))
  );
}

function isMarketLiquidityHeatmapBucket(
  value: unknown,
): value is MarketLiquidityHeatmapBucket {
  if (!isRecord(value)) {
    return false;
  }

  const cells = Reflect.get(value, 'cells');

  return (
    isPositiveNumber(Reflect.get(value, 'bestAskPrice')) &&
    isPositiveNumber(Reflect.get(value, 'bestBidPrice')) &&
    isPositiveNumber(Reflect.get(value, 'midPrice')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'timeStartMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'timeEndMs')) &&
    Array.isArray(cells) &&
    cells.every((cell) => isMarketLiquidityHeatmapCell(cell))
  );
}

function isMarketLiquidityHeatmapSnapshot(
  value: unknown,
): value is MarketLiquidityHeatmapSnapshot {
  if (!isRecord(value)) {
    return false;
  }

  const buckets = Reflect.get(value, 'buckets');
  const annotations = Reflect.get(value, 'annotations');

  return (
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'bucketDurationMs')) &&
    isPositiveNumber(Reflect.get(value, 'priceBucketSize')) &&
    isPositiveInteger(Reflect.get(value, 'windowMinutes')) &&
    isMarketHeatmapCoverage(Reflect.get(value, 'coverage')) &&
    isMarketHeatmapScale(Reflect.get(value, 'scale')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'snapshotTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'version')) &&
    Array.isArray(buckets) &&
    buckets.every((bucket) => isMarketLiquidityHeatmapBucket(bucket)) &&
    Array.isArray(annotations) &&
    annotations.every((annotation) =>
      isMarketLiquidityHeatmapAnnotation(annotation),
    )
  );
}

function isMarketLiquidityHeatmapSnapshotMessage(
  value: unknown,
): value is MarketLiquidityHeatmapSnapshotMessage {
  return (
    isMarketLiquidityHeatmapSnapshot(value) &&
    isRecord(value) &&
    Reflect.get(value, 'type') === 'market.liquidity_snapshot'
  );
}

function isMarketLiquidityHeatmapDeltaMessage(
  value: unknown,
): value is MarketLiquidityHeatmapDeltaMessage {
  if (!isRecord(value)) {
    return false;
  }

  const annotations = Reflect.get(value, 'annotations');

  return (
    Reflect.get(value, 'type') === 'market.liquidity_delta' &&
    isNonEmptyString(Reflect.get(value, 'asset')) &&
    isPositiveNumber(Reflect.get(value, 'priceBucketSize')) &&
    isPositiveInteger(Reflect.get(value, 'windowMinutes')) &&
    isMarketHeatmapCoverage(Reflect.get(value, 'coverage')) &&
    isMarketHeatmapScale(Reflect.get(value, 'scale')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'snapshotTimeMs')) &&
    isPositiveIntegerOrZero(Reflect.get(value, 'version')) &&
    isMarketLiquidityHeatmapBucket(Reflect.get(value, 'latestBucket')) &&
    Array.isArray(annotations) &&
    annotations.every((annotation) =>
      isMarketLiquidityHeatmapAnnotation(annotation),
    )
  );
}

function isMarketTradeSide(value: unknown): value is MarketTradeSideT {
  return value === 'buy' || value === 'sell';
}

function isMarketHeatmapCoverageStatus(
  value: unknown,
): value is MarketHeatmapCoverageStatusT {
  return value === 'empty' || value === 'full' || value === 'partial';
}

function isMarketHeatmapCaptureBasis(
  value: unknown,
): value is MarketHeatmapCaptureBasisT {
  return value === 'bucket_history' || value === 'event_history';
}

function isMarketHeatmapValueKind(
  value: unknown,
): value is MarketHeatmapValueKindT {
  return value === 'max_size' || value === 'notional';
}

function isMarketLiquidityHeatmapSide(
  value: unknown,
): value is MarketLiquidityHeatmapSideT {
  return value === 'ask' || value === 'bid';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isFiniteNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value);
}

function isNormalizedNumber(value: unknown) {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 1
  );
}

function isNonEmptyString(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0;
}

function isNullablePositiveIntegerOrZero(value: unknown) {
  return value === null || isPositiveIntegerOrZero(value);
}

function isPositiveInteger(value: unknown) {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function isPositiveIntegerOrZero(value: unknown) {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isPositiveNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isPositiveNumberOrZero(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}
